import React from 'react';

import { Header } from '@rbilabs/universal-components';
import { FlatList, View } from 'react-native';

import { centeredRowContainerStyles } from 'components/layout/centered-row-container';
import Link from 'components/link';
import { Calories } from 'components/price-and-calories';
import { useIsMobileBreakpoint } from 'hooks/use-media-query';
import {
  IMenuObjectWithContentOptions,
  useGetVariantContentOptions,
} from 'pages/menu/hooks/use-get-variant-content-options';
import { optionToUrl } from 'utils/menu';

import { MenuAcceleratorBadge } from '../menu-accelerator-badge';

import { MenuTileGridHeader } from './menu-tile-grid-header';
import { getCaloriesAndPrice } from './menu-tile/helpers';
import { useCalories } from './menu-tile/hooks';
import { MenuTile } from './menu-tile/menu-tiles-badges-experiment';
import { MainMenuObject } from './menu-tile/types';
import { Separator } from './separator';
import {
  StyledItemPrice,
  StyledListItemCalories,
  StyledMenuSectionTitle,
  StyledPriceAndCaloriesListWrapper,
  TileCard,
  TileFooter,
  TileImage,
} from './styled';
import { IMenuTileGridProps } from './types-badges-experiment';

// OLD (not one menu)
const MenuTileGrid = React.memo<IMenuTileGridProps>(
  ({ data = [], gridTitle, badges, pullToReloadProps, pullToReloadSpinner }) => {
    const getVariantContentOptions = useGetVariantContentOptions();
    const isMobile = useIsMobileBreakpoint();
    const getCalories = useCalories();
    return (
      <View>
        {pullToReloadSpinner}
        <FlatList
          testID="menu-tile-grid"
          contentContainerStyle={[centeredRowContainerStyles.content, { alignSelf: 'center' }]}
          key={isMobile ? 'mobile-list' : 'desktop-list'}
          data={data}
          {...pullToReloadProps}
          ListHeaderComponent={
            <>
              {gridTitle && <Header variant="headerThree">{gridTitle}</Header>}
              <MenuTileGridHeader />
            </>
          }
          ItemSeparatorComponent={isMobile ? Separator : null}
          ListFooterComponent={isMobile ? Separator : null}
          numColumns={isMobile ? 1 : 3}
          showsVerticalScrollIndicator={false}
          keyExtractor={mainMenuObject => mainMenuObject._id}
          renderItem={({ item }) => {
            const { image, name } = getVariantContentOptions(item as IMenuObjectWithContentOptions);

            const linkPath = optionToUrl(item);
            const { calories, price } = getCaloriesAndPrice(item as MainMenuObject, getCalories);
            const isMenuItem = item._type !== 'section';

            const badge = badges?.badges.has(item._id)
              ? { bonusPoints: badges.bonusPoints, pointsMultiplier: badges.pointsMultiplier }
              : undefined;

            let itemContent = (
              <MenuTile objectFitContain={isMenuItem} item={item as MainMenuObject} badge={badge} />
            );

            if (!isMobile) {
              itemContent = (
                <TileCard>
                  {!!badge && (
                    <MenuAcceleratorBadge
                      position="absolute"
                      bonusPoints={badge.bonusPoints}
                      pointsMultiplier={badge.pointsMultiplier}
                    />
                  )}

                  <Link
                    style={{ width: '100%', height: '100%', flexDirection: 'column' }}
                    linkPath={linkPath}
                    accessibilityLevel={3}
                    focusable={true}
                    accessibilityRole="header"
                    accessible={true}
                  >
                    <TileImage
                      image={image}
                      alt={item.imageDescription?.locale ?? 'menu item image'}
                      objectFitContain={isMenuItem}
                      importantForAccessibility="no"
                    />
                    <TileFooter isMenuItem={isMenuItem}>
                      <StyledMenuSectionTitle>{name?.locale}</StyledMenuSectionTitle>
                      {isMenuItem && (
                        <StyledPriceAndCaloriesListWrapper>
                          <StyledItemPrice>{price}</StyledItemPrice>
                          <Calories>
                            <StyledListItemCalories>{calories}</StyledListItemCalories>
                          </Calories>
                        </StyledPriceAndCaloriesListWrapper>
                      )}
                    </TileFooter>
                  </Link>
                </TileCard>
              );
            }

            return itemContent;
          }}
        />
      </View>
    );
  }
);

export default MenuTileGrid;
